import React, { forwardRef, ReactNode, useMemo } from 'react'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { ILocationSearchItemFragment } from 'transfix-maps/hooks/useLocationSearch'
import { IAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase'
import AutocompleteLocation, {
  IAutocompleteLocationProps,
} from 'transfix-ui/components/AutocompleteLocation'
import { IGeocodingResultTypeEnum } from 'types/graphqlTypes'
import { LocationSuggestion } from '../DPLSuggestLocation'
import { TGeocodingAllowedCountry, TGeocodingResultType } from './types'
import { getCountries } from './utils/getCountries'
import { getLocationLabel } from './utils/getLocationLabel'
import { getOptionLabel } from './utils/getOptionLabel'
import { getResultTypes } from './utils/getResultTypes'
import { getStateAutocompleteOption } from './utils/getStateAutocompleteOption'

export type TOmittedProp = 'value' | 'onChange' | 'countries' | 'resultTypes'
export interface ISuggestLocationProps extends Omit<IAutocompleteLocationProps, TOmittedProp> {
  /**
   * @example ['City', 'State']
   */
  resultTypes?: TGeocodingResultType[]
  /**
   * @example 'Brooklyn, NY'
   */
  placeholder?: string
  /**
   * @example { state: 'NY', city: 'Brooklyn', postalCode: '11201' }
   */
  value?: LocationSuggestion | null
  /**
   * @example 'location-input'
   */
  id?: string
  /**
   * @default false
   */
  required?: boolean
  /**
   * @example 'Pickup Location'
   */
  label?: ReactNode
  /**
   * @example ['US']
   */
  countries?: TGeocodingAllowedCountry[]
  /**
   * @example 'This field is required'
   */
  errorMessage?: string
  /**
   * @example 'Location should be in US'
   */
  validationMessageLabel?: string
  /**
   * @default false
   */
  large?: boolean
  /**
   * @example 'location-input'
   */
  dataTest?: string
  /**
   * @default false
   */
  invalid?: boolean
  /**
   * @default true
   */
  clearable?: boolean
  /**
   * @default false
   */
  disabled?: boolean
  /**
   * @example 'Location Input'
   */
  ariaLabel?: string
  onChange: (value: LocationSuggestion) => void
}

const isLocationSuggestion = (arg: LocationSuggestion | null | {}): arg is LocationSuggestion => {
  return !!get(arg, 'state')
}

const getValue = (
  value: LocationSuggestion | null | {}
): Omit<IAutocompleteBaseOption<ILocationSearchItemFragment>, 'groupIndex'> | null => {
  if (!value || isEqual({}, value) || !isLocationSuggestion(value)) {
    return null
  }

  const { city = '', postalCode = '', state = '' } = value || {}
  const text = getLocationLabel(value)
  return {
    id: [city, state, postalCode].filter(Boolean).join('-'),
    text,
    data: value as ILocationSearchItemFragment,
  }
}

const SuggestLocation = forwardRef<HTMLInputElement, ISuggestLocationProps>(
  (
    {
      classes,
      clearable = true,
      dataTest = 'AutocompleteLocation',
      errorMessage,
      getOptionLabel: getOptionLabelProp = getOptionLabel,
      invalid,
      large: largeProp,
      onChange: onChangeProp,
      placeholder: placeholderProp = '',
      resultTypes: resultTypesProp,
      countries: countriesProp,
      validationMessageLabel,
      value: valueProp = null,
      ...props
    },
    ref
  ) => {
    const value = getValue(valueProp)
    const countries = useMemo(() => getCountries(countriesProp), [countriesProp])
    const resultTypes = useMemo(() => getResultTypes(resultTypesProp), [resultTypesProp])

    const changeHandler: IAutocompleteLocationProps['onChange'] = newValue => {
      const { data } = newValue || {}
      const { resultType } = data || {}
      if (!newValue || !data) {
        onChangeProp({
          city: undefined,
          state: undefined,
          postalCode: undefined,
          countryCode: undefined,
          coords: undefined,
        })
      } else {
        const { city, countryCode, latitude: lat, longitude: lon, postalCode, stateCode: state } =
          data || {}

        const newLocation: LocationSuggestion = {
          state: state ?? '',
          countryCode: countryCode ?? '',
          coords: {
            lat: lat ?? 0,
            lon: lon ?? 0,
          },
        }

        if (resultType !== 'State') {
          newLocation.city = city ?? ''
          newLocation.postalCode = postalCode ?? ''
        }

        onChangeProp(newLocation)
      }
    }

    const addAdditionalOptionsProp = useMemo(() => {
      // Don't use hardcoded state options if resultType explicitly excludes state
      if (!resultTypes || resultTypes?.includes(IGeocodingResultTypeEnum.State)) {
        return getStateAutocompleteOption
      }
      return undefined
    }, [resultTypes])

    return (
      <AutocompleteLocation
        {...props}
        dataTest={dataTest}
        ref={ref}
        value={value}
        size={largeProp ? 'medium' : 'small'}
        placeholder={placeholderProp}
        disableClearable={!clearable}
        classes={classes}
        onChange={changeHandler}
        inputIcon='LocationFilled'
        renderInputType='withIcon'
        inputError={invalid || !!errorMessage}
        inputValidationMessage={validationMessageLabel || errorMessage}
        getOptionLabel={getOptionLabelProp}
        countries={countries}
        resultTypes={resultTypes}
        clearOnBlur
        autoSelect={false}
        addAdditionalOptions={addAdditionalOptionsProp}
        useAutocompleteLocationProps={{
          debounceMs: 100,
          filterExcludedSearchString: searchTerm => {
            const { length = 0 } = searchTerm ?? ''
            return searchTerm === '' || length < 3
          },
        }}
      />
    )
  }
)

export default SuggestLocation
